/**
 * @file order-summary-lines.ts
 * @description Строки-сводки карточки заказа в реестре (phase19.13).
 *
 * Задача карточки — дать менеджеру принять решение без захода в заказ:
 * оплачен ли, уехал ли, есть ли документы у юрлица. Логика собрана здесь
 * (чистые функции, покрыты тестами), рендер — в `OrderSummaryLines.tsx`.
 *
 * Инвариант: строка появляется только когда есть что сказать. Поля сводки
 * опциональные — сервер добавляет их постепенно, `undefined` = строки нет.
 */

import {
  ORDER_DELIVERY_CARRIER_LABEL,
  ORDER_DELIVERY_KIND_LABEL,
  ORDER_DELIVERY_SHIPMENT_STATUS_LABEL,
  ORDER_DOCUMENT_STATUS_LABEL,
  PAYMENT_METHOD_INLINE_LABEL,
  currencySymbol,
  formatMoney,
  type OrderDto,
  type OrderPaymentSummaryStatus,
  type PaymentMethod,
} from "./api-orders";

/** Тон строки — маппится в цвет на рендере. */
export type OrderSummaryTone = "muted" | "normal" | "success" | "warning" | "danger";

/**
 * Тексты про возврат средств — один источник правды для реестра, карточки
 * заказа и карточек отгрузок. Формулировки согласованы с пользователем
 * (решение 2026-08-14): менеджер не должен отгрузить товар клиенту, которому
 * уже вернули деньги.
 */
export const ORDER_REFUND_TEXT = {
  /** Строка-сводка в реестре заказов. */
  summary: "Средства возвращены",
  /** Алерт в шапке карточки заказа. */
  alert: "По заказу был возврат средств. Проверьте платежи перед отгрузкой.",
  /** Строка на карточке черновика отгрузки. */
  shipment: "Внимание: по заказу был возврат средств",
} as const;

/**
 * По заказу возвращали деньги.
 *
 * Сводка `order.payment` состояния REFUNDED не знает по определению
 * (`OrderPaymentSummaryStatus` без него) — признак живёт только в legacy-поле
 * `paymentStatus`, поэтому проверяем именно его.
 */
export function isOrderRefunded(order: Pick<OrderDto, "paymentStatus">): boolean {
  return order.paymentStatus === "REFUNDED";
}

/** Какую иконку lucide рисуем слева. */
export type OrderSummaryKind = "payment" | "delivery" | "documents";

export interface OrderSummaryLine {
  kind: OrderSummaryKind;
  tone: OrderSummaryTone;
  text: string;
}

/**
 * Способ оплаты. Сервер может прислать машинный код (`CASH`) или уже готовую
 * подпись («наличные») — реестр покрывает первый случай, остальное как есть.
 */
export function resolvePaymentMethodLabel(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const code = trimmed.toUpperCase();
  return PAYMENT_METHOD_INLINE_LABEL[code as PaymentMethod] ?? trimmed;
}

/** Юрлицо: по новому полю, а до его появления — по наличию legalEntityId. */
export function isLegalEntityOrder(order: OrderDto): boolean {
  if (order.buyerKind) return order.buyerKind === "LEGAL_ENTITY";
  return order.legalEntityId != null;
}

/** Телефон в человеческом виде: +7 981 750-55-00. Нераспознанный — как есть. */
export function formatOrderPhone(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const digits = raw.replace(/\D/g, "");
  const ru =
    digits.length === 11 && (digits.startsWith("7") || digits.startsWith("8"))
      ? `7${digits.slice(1)}`
      : digits.length === 10
        ? `7${digits}`
        : null;
  if (!ru) return raw.trim() || null;
  return `+7 ${ru.slice(1, 4)} ${ru.slice(4, 7)}-${ru.slice(7, 9)}-${ru.slice(9, 11)}`;
}

/** Строка оплаты: главный вопрос карточки — «деньги пришли?». */
export function buildPaymentLine(order: OrderDto): OrderSummaryLine | null {
  const symbol = currencySymbol(order.currency);
  const summary = order.payment;
  const method = resolvePaymentMethodLabel(summary?.lastMethod);

  // Возврат средств живёт вне новой сводки и перебивает её: после возврата
  // «оплачен полностью» в реестре ввело бы менеджера в заблуждение.
  if (isOrderRefunded(order)) {
    return { kind: "payment", tone: "danger", text: ORDER_REFUND_TEXT.summary };
  }

  // Пока сводки нет — читаем legacy-поле paymentStatus.
  const status: OrderPaymentSummaryStatus = summary
    ? summary.status
    : order.paymentStatus === "FULL" || order.paymentStatus === "PARTIAL"
      ? order.paymentStatus
      : "NONE";

  if (status === "FULL") {
    return {
      kind: "payment",
      tone: "success",
      text: method ? `Оплачен полностью · ${method}` : "Оплачен полностью",
    };
  }

  if (status === "PARTIAL") {
    // Без сводки суммы нет — говорим хотя бы факт частичной оплаты.
    const text = summary
      ? `Частично ${formatMoney(String(summary.paidTotal))} из ${formatMoney(order.totalGross)} ${symbol}`
      : "Оплачен частично";
    return { kind: "payment", tone: "warning", text };
  }

  const postpay = order.paymentTerms === "POSTPAY";
  return {
    kind: "payment",
    tone: "muted",
    text: postpay ? "Не оплачен · при получении" : "Не оплачен",
  };
}

/** Строка доставки: чем, куда и где посылка сейчас. */
export function buildDeliveryLine(order: OrderDto): OrderSummaryLine | null {
  const delivery = order.delivery;
  if (!delivery) return null;

  if (delivery.kind === "PICKUP") {
    return { kind: "delivery", tone: "normal", text: ORDER_DELIVERY_KIND_LABEL.PICKUP };
  }

  const parts: string[] = [];
  if (delivery.carrier) parts.push(ORDER_DELIVERY_CARRIER_LABEL[delivery.carrier]);
  if (delivery.kind) parts.push(ORDER_DELIVERY_KIND_LABEL[delivery.kind]);
  parts.push(ORDER_DELIVERY_SHIPMENT_STATUS_LABEL[delivery.shipmentStatus]);
  if (delivery.trackNumber) parts.push(`трек ${delivery.trackNumber}`);

  const tone: OrderSummaryTone =
    delivery.shipmentStatus === "DELIVERED"
      ? "success"
      : delivery.shipmentStatus === "NONE" || delivery.shipmentStatus === "DRAFT"
        ? "muted"
        : "normal";

  return { kind: "delivery", tone, text: parts.join(" · ") };
}

/** Строка документов — только для юрлица: их отсутствие само по себе сигнал. */
export function buildDocumentsLine(order: OrderDto): OrderSummaryLine | null {
  if (!isLegalEntityOrder(order)) return null;
  if (order.documents === undefined) return null;

  const docs = order.documents;
  if (!docs || (docs.invoiceStatus === "NONE" && docs.updStatus === "NONE")) {
    return { kind: "documents", tone: "warning", text: "Документы не созданы" };
  }

  const text =
    `Счёт: ${ORDER_DOCUMENT_STATUS_LABEL[docs.invoiceStatus]}` +
    ` · УПД: ${ORDER_DOCUMENT_STATUS_LABEL[docs.updStatus]}`;
  const hasGap = docs.invoiceStatus === "NONE" || docs.updStatus === "NONE";
  const allSigned = docs.invoiceStatus === "SIGNED" && docs.updStatus === "SIGNED";

  return {
    kind: "documents",
    tone: hasGap ? "warning" : allSigned ? "success" : "normal",
    text,
  };
}

/** Все строки карточки в порядке макета: оплата → доставка → документы. */
export function buildOrderSummaryLines(order: OrderDto): OrderSummaryLine[] {
  return [buildPaymentLine(order), buildDeliveryLine(order), buildDocumentsLine(order)].filter(
    (line): line is OrderSummaryLine => line !== null,
  );
}
